import { supabase } from '@/integrations/supabase/client';
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import type {
  ProfileForGestao,
  UserUpdateData,
  LeadsDoUsuarioResponse,
  TransferirLeadResponse,
} from '@/types/users';
import type { UserRole } from '@/types/permissions';

const COLS = 'id, nome, role, ativo, telefone, cidade, estado, cpf, pix, created_at, updated_at, vendedor_id';

function mapRow(row: Record<string, unknown>): ProfileForGestao {
  return {
    id: row.id as string,
    nome: (row.nome as string) ?? '',
    role: row.role as UserRole,
    ativo: Boolean(row.ativo),
    telefone: (row.telefone as string) ?? null,
    cidade: (row.cidade as string) ?? null,
    estado: (row.estado as string) ?? null,
    cpf: (row.cpf as string) ?? null,
    pix: (row.pix as string) ?? null,
    created_at: (row.created_at as string) ?? '',
    updated_at: (row.updated_at as string) ?? '',
    vendedor_id: (row.vendedor_id as string) ?? null,
  };
}

/**
 * Lista todos os perfis para gestão (admin). Ordenado por nome.
 */
export async function listarUsuarios(): Promise<ProfileForGestao[]> {
  const { data, error } = await supabase
    .from('profiles')
    .select(COLS)
    .order('nome', { ascending: true });

  if (error) {
    console.error('Erro ao listar usuários:', error);
    throw new Error(error.message || 'Erro ao listar usuários');
  }

  return (data ?? []).map(mapRow);
}

/**
 * Atualiza um perfil (dados cadastrais e/ou role). Apenas admin.
 */
export async function atualizarUsuario(userId: string, data: UserUpdateData): Promise<void> {
  const update: Record<string, unknown> = {};
  if (data.nome !== undefined) update.nome = data.nome;
  if (data.role !== undefined) update.role = data.role;
  if (data.ativo !== undefined) update.ativo = data.ativo;
  if (data.telefone !== undefined) update.telefone = data.telefone || null;
  if (data.vendedor_id !== undefined) update.vendedor_id = data.vendedor_id || null;
  if (data.cidade !== undefined) update.cidade = data.cidade ?? null;
  if (data.estado !== undefined) update.estado = data.estado ?? null;
  if (data.cpf !== undefined) update.cpf = data.cpf ?? null;
  if (data.pix !== undefined) update.pix = data.pix ?? null;

  const { error } = await supabase
    .from('profiles')
    .update(update)
    .eq('id', userId);

  if (error) {
    console.error('Erro ao atualizar usuário:', error);
    throw new Error(error.message || 'Erro ao atualizar usuário');
  }
}

/**
 * Altera o tipo de acesso (role) de um usuário. Apenas admin.
 */
export async function alterarTipoAcesso(userId: string, role: UserRole): Promise<void> {
  await atualizarUsuario(userId, { role });
}

/**
 * Desativa um usuário (ativo = false). Não remove o registro. Apenas admin.
 */
export async function desativarUsuario(userId: string): Promise<void> {
  await atualizarUsuario(userId, { ativo: false });
}

/**
 * Exclui permanentemente um usuário do sistema.
 * Remove perfil, auth e libera o e-mail para reutilização em novos cadastros.
 * Usa a rota apropriada conforme o role (parceiro, vendedor ou outros).
 */
export async function excluirUsuario(userId: string, role: UserRole): Promise<void> {
  let url: string;
  if (role === 'parceiro') {
    url = `/api/parceiros/${userId}`;
  } else if (role === 'vendedor') {
    url = `/api/vendedores/${userId}`;
  } else {
    url = `/api/users/${userId}`;
  }
  const res = await fetchWithAuth(url, { method: 'DELETE' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error((data as { error?: string }).error || 'Erro ao excluir usuário');
  }
}

/**
 * Lista leads associados a um usuário (admin).
 */
export async function listarLeadsDoUsuario(userId: string): Promise<LeadsDoUsuarioResponse> {
  const res = await fetchWithAuth(`/api/users/${userId}/leads`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error((data as { error?: string }).error || 'Erro ao listar leads do usuário');
  }
  return data as LeadsDoUsuarioResponse;
}

/**
 * Transfere um lead para outro usuário (admin).
 */
export async function transferirLead(
  leadId: string,
  novoUsuarioId: string
): Promise<TransferirLeadResponse> {
  const res = await fetchWithAuth(`/api/leads/${leadId}/transferir`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ novoUsuarioId }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error((data as { error?: string }).error || 'Erro ao transferir lead');
  }
  return data as TransferirLeadResponse;
}

/**
 * Exclui permanentemente um lead (admin).
 */
export async function excluirLead(leadId: string): Promise<void> {
  const res = await fetchWithAuth(`/api/leads/${leadId}`, { method: 'DELETE' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error((data as { error?: string }).error || 'Erro ao excluir lead');
  }
}
